import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { capitalize } from 'lodash';
import cx from 'classnames';

import { Spinner, IconButton, Tooltip } from 'components';
import { CancelableBannerInfo, NewPostModal, Post, PostImageUploader, PostModal, PostReply } from 'components/discussions';
import { PostSummary, AddPollModal } from 'components/candidate/discussions';
import Avatar from 'components/Avatar';
import { TextArea } from 'components/form';
import { useResponsive } from 'hooks/useResponsive';
import { useDiscussions } from 'lib/providers/DiscussionsProvider';
import { useDiscussion } from 'lib/providers/DiscussionProvider';
import { useAuth } from 'lib/providers/AuthProvider';
import { TMediaPostPayload, TPostType, TTextPostPayload, TUserableType } from 'lib/models/discussion';
import { getBasicPostParticipant } from 'lib/utils/discussion';
import { TPostParticipant } from 'lib/models/student-society';
import { useCandidateMessage } from 'lib/providers/CandidateMessageProvider';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from './SocietyDiscussions.module.scss';

const SocietyDiscussions: React.FC = () => {
  const [t] = useTranslation('discussions');
  const { user } = useAuth();
  const screens = useResponsive();
  const {
    studentSociety,
    posts,
    isPostsLoading,
    addPost,
    societyDiscussionsSettings,
    showAdminApprovalInfo,
    clearAdminApprovalInfo,
    societyName,
    isSocietyMember,
    openNewPostModal,
  } = useDiscussions();
  const { post: selectedPost, clearCurrentPost, setCurrentThread, openPostModal } = useDiscussion();
  const { onUpdateLastPost, onReadPost } = useCandidateMessage();
  const [postContent, setPostContent] = useState('');
  const [postType, setPostType] = useState<TPostType>(TPostType.TextPost);
  const [postInputFocussed, setPostInputFocussed] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);
  const [isAddPollModalOpen, setIsAddPollModalOpen] = useState(false);
  const [postImages, setPostImages] = useState<TMediaPostPayload['media']>([]);

  const handlePostContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setPostContent(e.target.value);
    },
    [setPostContent]
  );

  const onSubmitPost = useCallback(async () => {
    if (!postContent || !user) return;

    const basicPayload = {
      type: postType,
      content: postContent,
      userable_type: capitalize(user.userable_type) as TUserableType,
      who_can_reply: societyDiscussionsSettings.who_can_reply_to_posts,
      parent_post_id: selectedPost?.id,
      media: postImages.length > 0 ? postImages : undefined,
    } as TTextPostPayload;
    try {
      const newPost = await addPost(basicPayload);
      if (!selectedPost && newPost && !societyDiscussionsSettings.is_post_approval_required) {
        onUpdateLastPost(newPost);
        mainRef.current?.scrollTo({ top: 0 });
      }
      setPostContent('');
      setPostType(TPostType.TextPost);
      setPostImages([]);
    } catch (error) {
      console.error('Error adding post', error);
    }
  }, [user, addPost, postType, postContent, societyDiscussionsSettings, selectedPost, onUpdateLastPost, postImages]);

  const postPlaceholder = useMemo(() => {
    if (!selectedPost) return t('discussions-in-messages.new-discussion.placeholder');

    const postedByUser = getBasicPostParticipant(selectedPost);
    if (user?.id === postedByUser.id) return t('discussions-in-messages.reply.placeholder');

    return t('discussions-in-messages.reply.to-placeholder', { name: postedByUser.name });
  }, [user, selectedPost]);

  const canPost = useMemo(() => {
    if (selectedPost) {
      return (
        selectedPost?.who_can_reply === TPostParticipant.ANYONE || (selectedPost?.who_can_reply === TPostParticipant.MEMBERS_ONLY && isSocietyMember)
      );
    } else {
      return (
        societyDiscussionsSettings.who_can_post === TPostParticipant.ANYONE ||
        (societyDiscussionsSettings.who_can_post === TPostParticipant.MEMBERS_ONLY && isSocietyMember)
      );
    }
  }, [selectedPost, societyDiscussionsSettings, isSocietyMember]);

  const societyPublicURL = useMemo(() => {
    const societySlug = studentSociety.slug;
    const route = PAGE_ROUTES.SOCIETY_PUBLIC.replace('[slug]', societySlug);
    return route;
  }, [studentSociety]);

  const handleInputFocus = useCallback(() => {
    if (screens.sm) {
      if (selectedPost) {
        openPostModal();
      } else {
        openNewPostModal();
      }
    } else {
      setPostInputFocussed(true);
    }
  }, [selectedPost, setPostInputFocussed, screens]);

  const handleInputBlur = useCallback(() => {
    if (!screens.sm) {
      setPostInputFocussed(false);
    }
  }, [setPostInputFocussed, screens]);

  const handlePollClick = useCallback(() => {
    setIsAddPollModalOpen(true);
  }, [screens.sm, setIsAddPollModalOpen, openPostModal]);

  const onCloseAddPollModal = useCallback(
    ({ postCreated } = { postCreated: false }) => {
      if (postCreated) {
        setPostContent('');
      }
      setIsAddPollModalOpen(false);
    },
    [setIsAddPollModalOpen]
  );

  const onImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setPostType(TPostType.MediaPost);
    setPostImages((prev) => [...prev, ...files]);
  }, []);

  const onImageRemove = useCallback(
    (index: number) => {
      if (postImages.length === 1) {
        setPostType(TPostType.TextPost);
      }
      setPostImages((prev) => {
        const updatedImages = [...prev];
        updatedImages.splice(index, 1);
        return updatedImages;
      });
    },
    [postImages, setPostType]
  );

  return (
    <section className={styles.container}>
      <header className={cx(styles.header, { [styles['post-replies']]: selectedPost?.comments.length })}>
        {selectedPost ? (
          <>
            <IconButton iconName="icon_chevron-left" size="auto" className={styles.back} onClick={clearCurrentPost} />
            <div className={styles['post-summary-container']}>
              <PostSummary post={selectedPost} className={styles.summary} contentClassName={styles['summary-content']} />
              {selectedPost.comments.length > 0 ? (
                <span className={styles['stat']}>
                  {t('discussions-in-messages.post.replies_count', {
                    count: selectedPost.comments.length,
                  })}
                </span>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <Avatar className={styles.avatar} name={studentSociety.name} size="small" icon={studentSociety.icon_url} />
            <a className={styles.title} href={societyPublicURL} target="_blank" rel="noreferrer">
              {studentSociety.name}
            </a>
          </>
        )}
      </header>
      <main className={styles.main} ref={mainRef}>
        {selectedPost
          ? selectedPost.comments.map((postReply) => (
              <PostReply key={postReply.id} postReply={postReply} postId={selectedPost.id} className={styles.post} />
            ))
          : posts.map((post) => (
              <Post key={post.id} post={post} showReplies={false} className={styles.post} onReplyClick={setCurrentThread} onReadPost={onReadPost} />
            ))}

        {isPostsLoading && <Spinner wrapperClassname={styles.spinner} />}
      </main>
      {canPost && !selectedPost && showAdminApprovalInfo ? (
        <CancelableBannerInfo onCancel={clearAdminApprovalInfo} className={styles.banner}>
          {t('discussions-in-messages.new-discussion.info-holder', { society_name: societyName })}
        </CancelableBannerInfo>
      ) : null}
      {canPost ? (
        <footer className={styles.footer}>
          {postImages.length > 0 ? (
            <div className={styles.images}>
              {postImages.map((image, index) => (
                <div className={styles['post-image-container']} key={index}>
                  <img src={URL.createObjectURL(image)} alt="post-image" className={styles.image} />
                  <IconButton iconName="close" size="xsmall" className={styles.icon} onClick={() => onImageRemove(index)} />
                </div>
              ))}
              <div className={styles['post-image-container']}>
                <input type="file" accept="image/*" id="upload-post-image" multiple onChange={onImageUpload} hidden />
                <IconButton iconName="plus" size="xsmall" className={styles.icon} htmlFor="upload-post-image" />
              </div>
            </div>
          ) : null}
          <div className={styles['footer-content']}>
            <div className={styles['post-content-container']}>
              <TextArea
                textAreaClassName={styles['post-content']}
                placeholder={postPlaceholder}
                initialHeight="46px"
                isDynamic={true}
                value={postContent}
                onChange={handlePostContentChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              {postType === TPostType.TextPost ? (
                <div className={cx(styles.actions, { [styles.active]: postInputFocussed })}>
                  <Tooltip content={t('add-photo')} color="dark" xAlign="center" position="top" isRounded>
                    <PostImageUploader
                      className={styles['action-icon-btn']}
                      images={postImages}
                      onImageUpload={onImageUpload}
                      onRemove={onImageRemove}
                      renderImages={false}
                    />
                  </Tooltip>
                  <Tooltip content={t('add-poll')} color="dark" xAlign="center" position="top" isRounded>
                    <IconButton iconName="unordered_list" size="auto" className={styles['action-icon-btn']} onClick={handlePollClick} />
                  </Tooltip>
                </div>
              ) : null}
            </div>
            {postContent && (
              <IconButton className={styles['send-button']} iconClassName={styles['send-icon']} iconName="icon_arrow-up-2" onClick={onSubmitPost} />
            )}
          </div>
        </footer>
      ) : null}
      {screens.sm ? (
        <>
          <NewPostModal />
          <PostModal />
        </>
      ) : (
        <AddPollModal placeholder={postPlaceholder} open={isAddPollModalOpen} onClose={onCloseAddPollModal} />
      )}
    </section>
  );
};

SocietyDiscussions.displayName = 'SocietyDiscussions';

export default SocietyDiscussions;
